import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, FileText, Pencil, Save, X, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import TaxBadge from "@/components/app/TaxBadge";

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [folders, setFolders] = useState([]);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [folderId, setFolderId] = useState("");

  useEffect(() => {
    api.get(`/documents/${id}`).then(r => {
      setDoc(r.data);
      setName(r.data.smart_filename.replace(/\.[^/.]+$/, ""));
      setFolderId(r.data.folder_id || "");
    });
    api.get("/folders").then(r => setFolders(r.data));
  }, [id]);

  const save = async () => {
    try {
      const r = await api.patch(`/documents/${id}`, { smart_filename: name, folder_id: folderId });
      setDoc(r.data);
      setEditing(false);
      toast.success("Documento aggiornato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo documento?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Documento eliminato");
      nav("/app/documenti");
    } catch { toast.error("Errore"); }
  };

  const setTax = async (relevance) => {
    try {
      const r = await api.patch(`/documents/${id}`, { tax_relevance: relevance });
      setDoc(r.data);
      setFolderId(r.data.folder_id || "");
      if (relevance === "none") {
        toast.success("Rimosso dalla sezione fiscale");
      } else {
        const label = relevance === "isee" ? "ISEE" : relevance === "730_cu" ? "730 / CU" : "ISEE + 730/CU";
        toast.success(`Aggiunto alla sezione fiscale (${label})`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  if (!doc) return <div className="p-8 text-sm">Caricamento…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/app/documenti" data-testid="detail-back-link" className="text-sm flex items-center gap-2 text-[#5C5C5C] hover:text-[#0A0A0A] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Torna all'archivio
        </Link>
        <div className="flex gap-2">
          <a href={fileUrl(doc.id)} download={doc.smart_filename} target="_blank" rel="noreferrer">
            <Button data-testid="detail-download-btn" variant="outline" className="rounded-xl border-black/20 hover:bg-black/5 h-9">
              <Download className="w-4 h-4 mr-2" /> Scarica
            </Button>
          </a>
          <Button
            data-testid="detail-delete-btn"
            variant="outline"
            onClick={remove}
            className="rounded-xl border-[#FF3333]/30 text-[#FF3333] hover:bg-[#FF3333]/5 h-9"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Elimina
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Preview */}
        <div className="lg:col-span-2 bg-white border border-black/10 rounded-xl p-6">
          <div className="overline text-[#8A8A8A] mb-4">ANTEPRIMA</div>
          <div className="bg-[#FAFAFA] border border-black/10 rounded-xl overflow-hidden min-h-[500px] flex items-center justify-center">
            {doc.mime_type?.startsWith("image/") ? (
              <img src={fileUrl(doc.id)} alt={doc.smart_filename} className="max-w-full max-h-[70vh] object-contain" />
            ) : (
              <div className="p-16 text-center">
                <FileText className="w-24 h-24 text-black/15 mx-auto mb-4" />
                <p className="text-sm text-[#5C5C5C] mb-4">{doc.mime_type}</p>
                <a href={fileUrl(doc.id)} target="_blank" rel="noreferrer">
                  <Button className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white">
                    <Download className="w-4 h-4 mr-2" /> Apri file
                  </Button>
                </a>
              </div>
            )}
          </div>

          {doc.extracted_text && (
            <div className="mt-6">
              <div className="overline text-[#8A8A8A] mb-3">TESTO ESTRATTO</div>
              <div className="bg-[#FAFAFA] border border-black/10 rounded-xl p-4 font-mono-plex text-xs whitespace-pre-wrap max-h-64 overflow-auto" data-testid="detail-extracted-text">
                {doc.extracted_text}
              </div>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="bg-white border border-black/10 rounded-xl p-6 h-fit">
          <div className="flex items-center justify-between mb-6">
            <div className="overline text-[#8A8A8A]">DETTAGLI</div>
            {editing ? (
              <div className="flex gap-1">
                <button data-testid="detail-cancel-edit" onClick={() => { setEditing(false); setName(doc.smart_filename.replace(/\.[^/.]+$/, "")); setFolderId(doc.folder_id || ""); }} className="p-1.5 hover:bg-black/5 rounded-xl"><X className="w-3.5 h-3.5" /></button>
                <button data-testid="detail-save-edit" onClick={save} className="p-1.5 bg-[#0A0A0A] text-white hover:bg-[#1A1A1A] rounded-xl"><Save className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <button data-testid="detail-edit-btn" onClick={() => setEditing(true)} className="p-1.5 hover:bg-black/5 rounded-xl"><Pencil className="w-3.5 h-3.5" /></button>
            )}
          </div>

          <div className="space-y-4">
            {doc.tax_relevance && doc.tax_relevance !== "none" ? (
              <div className="border border-[#009966]/25 bg-[#009966]/5 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <TaxBadge relevance={doc.tax_relevance} size="lg" />
                  <button
                    data-testid="tax-remove-btn"
                    onClick={() => setTax("none")}
                    className="text-[10px] uppercase tracking-widest text-[#8A8A8A] hover:text-[#FF3333] transition-colors"
                    title="Rimuovi dalla sezione fiscale"
                  >
                    Rimuovi
                  </button>
                </div>
                <div className="text-xs text-[#5C5C5C]">Utile per il commercialista — già nella cartella dedicata.</div>
                {doc.tax_reason && <div className="text-xs text-[#5C5C5C] mt-2 italic">{doc.tax_reason}</div>}
              </div>
            ) : (
              <div className="border border-dashed border-[#0A0A0A]/30 rounded-xl p-4 bg-[#0A0A0A]/5" data-testid="tax-add-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#0A0A0A]" />
                  <div className="overline text-[#0A0A0A]">SEZIONE FISCALE</div>
                </div>
                <div className="text-xs text-[#5C5C5C] mb-3 leading-relaxed">
                  Aggiungilo alla cartella del commercialista se è utile per la dichiarazione.
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    data-testid="tax-add-isee"
                    onClick={() => setTax("isee")}
                    variant="outline"
                    className="rounded-xl h-9 text-xs border-[#009966]/40 hover:bg-[#009966]/10 hover:text-[#009966]"
                  >ISEE</Button>
                  <Button
                    data-testid="tax-add-730"
                    onClick={() => setTax("730_cu")}
                    className="rounded-xl h-9 text-xs bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white"
                  >730 / CU</Button>
                  <Button
                    data-testid="tax-add-both"
                    onClick={() => setTax("both")}
                    variant="outline"
                    className="rounded-xl h-9 text-xs border-[#8A2BE2]/40 hover:bg-[#8A2BE2]/10 hover:text-[#8A2BE2]"
                  >Entrambi</Button>
                </div>
              </div>
            )}
            <Field label="NOME FILE">
              {editing ? (
                <Input data-testid="detail-name-input" value={name} onChange={e => setName(e.target.value)} className="rounded-xl h-9 bg-white focus-visible:ring-[#0A0A0A] border-black/15" />
              ) : (
                <div className="font-medium text-sm break-all">{doc.smart_filename}</div>
              )}
            </Field>

            <Field label="CARTELLA">
              {editing ? (
                <Select value={folderId} onValueChange={setFolderId}>
                  <SelectTrigger data-testid="detail-folder-select" className="rounded-xl h-9 border-black/15"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm">{doc.folder_name || doc.category}</div>
              )}
            </Field>

            <Field label="TIPO"><div className="text-sm">{doc.document_type}</div></Field>
            <Field label="ESERCENTE"><div className="text-sm">{doc.vendor || "—"}</div></Field>
            <Field label="DATA DOCUMENTO"><div className="text-sm font-mono-plex">{doc.document_date || "—"}</div></Field>
            <Field label="IMPORTO TOTALE">
              <div className="text-lg font-display font-bold">
                {doc.total_amount != null ? `${doc.currency || "€"} ${Number(doc.total_amount).toFixed(2)}` : "—"}
              </div>
            </Field>
            {doc.metadata?.invoice_number && (
              <Field label="N. FATTURA"><div className="text-sm font-mono-plex">{doc.metadata.invoice_number}</div></Field>
            )}
            {doc.metadata?.vat_number && (
              <Field label="P.IVA"><div className="text-sm font-mono-plex">{doc.metadata.vat_number}</div></Field>
            )}
            {doc.metadata?.summary && (
              <Field label="RIASSUNTO"><div className="text-sm text-[#5C5C5C] leading-relaxed">{doc.metadata.summary}</div></Field>
            )}
          </div>
        </div>
      </div>

      {doc.metadata?.items?.length > 0 && (
        <div className="mt-6 bg-white border border-black/10 rounded-xl">
          <div className="border-b border-black/10 px-6 py-4">
            <div className="overline text-[#8A8A8A]">RIGHE ARTICOLI</div>
          </div>
          <div className="divide-y divide-black/5">
            {doc.metadata.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 px-6 py-3 text-sm">
                <div className="col-span-10">{it.description}</div>
                <div className="col-span-2 text-right font-mono-plex">
                  {it.amount != null ? `€ ${Number(it.amount).toFixed(2)}` : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="border-b border-black/5 pb-3 last:border-b-0">
      <div className="overline text-[#8A8A8A] mb-1.5">{label}</div>
      {children}
    </div>
  );
}
